/* eslint-disable consistent-return */
/* eslint-disable import/no-extraneous-dependencies */
import { parse, subYears } from 'date-fns';
import * as yup from 'yup';

export default function translatedFormValidationSchemas(
  t: Function,
  lang?: String
) {
  const firstName = t('dynamicFieldValue.firstName');
  const lastName = t('dynamicFieldValue.lastName');
  const userName = t('dynamicFieldValue.userName');
  const currentPinText = t('dynamicFieldValue.currentPin');
  const newPinText = t('dynamicFieldValue.newPin');
  const confirmPinText = t('dynamicFieldValue.confirmPin');
  const emailText = t('dynamicFieldValue.email');
  const phoneNumber = t('dynamicFieldValue.phoneNumber');
  const dateText = t('dynamicFieldValue.date');
  const dateOfBirth = t('dynamicFieldValue.dateOfBirth');
  const designation = t('dynamicFieldValue.designation');
  const groupName = t('dynamicFieldValue.groupName');
  const description = t('dynamicFieldValue.description');
  const customSelect = t('dynamicFieldValue.customSelect');

  const FormValidationSchemas: any = {
    first_name: yup
      .string()
      .nullable()
      .required(() =>
        t('requiredErrorMessage', {
          field: firstName,
        })
      )
      .matches(/\S/, () => t('matchesErrorMessage', { field: firstName }))
      .max(30, () => t('maxErrorMessage', { field: firstName, maxValue: 30 }))
      .min(3, () => t('minErrorMessage', { field: firstName, minValue: 3 })),
    last_name: yup
      .string()
      .nullable()
      .required(() =>
        t('requiredErrorMessage', {
          field: lastName,
        })
      )
      .matches(/\S/, () => t('matchesErrorMessage', { field: lastName }))
      .max(30, () => t('maxErrorMessage', { field: lastName, maxValue: 30 }))
      .min(3, () => t('minErrorMessage', { field: lastName, minValue: 3 })),
    username: yup
      .string()
      .nullable()
      .required(() =>
        t('requiredErrorMessage', {
          field: userName,
        })
      )
      .matches(/\S/, () => t('matchesErrorMessage', { field: userName }))
      .max(30, () => t('maxErrorMessage', { field: userName, maxValue: 30 }))
      .min(5, t('minErrorMessage', { field: userName, minValue: 5 })),
    currentPin: yup
      .string()
      .nullable()
      .required(() => t('requiredErrorMessage', { field: currentPinText }))
      .typeError(() => t('requiredErrorMessage', { field: currentPinText }))
      .matches(/^\S*$/, () => t('whiteSpaceErrorMessage'))
      .matches(/^[0-9]+$/, () => t('onlyNumbersAllowed'))
      .max(4, () =>
        t('maxErrorMessage', { field: currentPinText, maxValue: 4 })
      )
      .min(4, () =>
        t('minErrorMessage', { field: currentPinText, minValue: 4 })
      ),
    newPin: yup
      .string()
      .nullable()
      .required(() => t('requiredErrorMessage', { field: newPinText }))
      .typeError(() => t('requiredErrorMessage', { field: newPinText }))
      .matches(/^\S*$/, () => t('whiteSpaceErrorMessage'))
      .matches(/^[0-9]+$/, () => t('onlyNumbersAllowed'))
      .max(4, () => t('maxErrorMessage', { field: newPinText, maxValue: 4 }))
      .min(4, () => t('minErrorMessage', { field: newPinText, minValue: 4 })),
    confirmPin: yup
      .string()
      .nullable()
      .required(() => t('requiredErrorMessage', { field: confirmPinText }))
      .typeError(() => t('requiredErrorMessage', { field: confirmPinText }))
      .matches(/^\S*$/, () => t('whiteSpaceErrorMessage'))
      .matches(/^[0-9]+$/, () => t('onlyNumbersAllowed'))
      .when('newPin', (newPin, schema): any => {
        if (newPin)
          return schema
            .required(() =>
              t('requiredErrorMessage', { field: confirmPinText })
            )
            .test(
              'Match Pin',
              () => t('doesNotMatchError', { field: 'PIN' }),
              (confirmNewPinValue: string | null, formValues: any) => {
                return confirmNewPinValue === formValues.parent.newPin;
              }
            );
      })
      .max(4, () =>
        t('maxErrorMessage', { field: confirmPinText, maxValue: 4 })
      )
      .min(4, () =>
        t('minErrorMessage', { field: confirmPinText, minValue: 4 })
      ),
    email: yup
      .string()
      .email(() => t('invalidError', { field: emailText }))
      .nullable()
      .test(
        'Email conditional validation',
        () => t('requiredErrorMessage', { field: emailText }),
        (defaultDate: any, { parent }: any) => {
          return Boolean(defaultDate) || Boolean(parent?.phone);
        }
      ),
    phone: yup
      .string()
      // .typeError("Streak and Goals Target is required")
      .nullable()
      .test(
        'Phone number conditional validation',
        () => t('requiredErrorMessage', { field: phoneNumber }),
        (defaultDate: any, { parent }: any) => {
          return Boolean(defaultDate) || Boolean(parent?.email);
        }
      ),
    dateofbirth: yup
      .date()
      .nullable()
      .typeError(() => t('invalidError', { field: dateText })) // Replace t function with your translation function as needed
      .min(parse('01/01/1900', 'MM/dd/yyyy', new Date()), () =>
        t('dateMinError', { field: dateOfBirth, minDate: '01/01/1900' })
      ) // Adjust minimum date as needed
      .max(subYears(new Date(), 0), () =>
        t('dateMaxError', { field: dateOfBirth })
      ) // Ensures the date is not in the future
      .required(() => t('requiredErrorMessage', { field: dateOfBirth })),
    designation: yup
      .object()
      .nullable()
      .required(() => t('designationRequired', { field: designation })),
    groupName: yup
      .string()
      .nullable()
      .required(() => t('groupNameRequired', { field: 'groupName' }))
      .matches(/\S/, () => t('groupNameRequired'))
      .max(30, () => t('canNotBeLonger', { field: 'groupName', maxValue: 30 })),
    description: yup
      .string()
      .nullable()
      .required(() => t('descriptionIsRequired', { field: 'description' }))
      .matches(/\S/, () => t('descriptionIsRequired'))
      .max(100, () =>
        t('dscCanNotBeLonger', { field: 'description', maxValue: 100 })
      ),
    customSelect: yup.array().min(1, 'Please select at least one option.'),
    topicName: yup
      .object()
      .nullable()
      .required(() => t('topicIsRequired', { field: 'topicName' })),
    message: yup
      .string()
      .nullable()
      .required(() => t('messageIsRequired', { field: 'message' }))
      .max(2000, () =>
        t('messageMaxLength', { field: 'message', maxValue: 2000 })
      )
      .min(10, () =>
        t('messageMinLength', { field: confirmPinText, minValue: 10 })
      ),
  };

  return FormValidationSchemas;
}
